import type { JSX } from 'react';

import type { PrefetchedDataProps, PrefetchedUserDataProps } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import {
	type PetitionDetailsPagePrefetchedContext,
	type PetitionDetailsPagePrefetchedUserContext,
	prefetchPageContext,
	prefetchPageUserContext,
} from 'src/app/pages/petitionDetails/pageContext';
import type { PetitionHeadlineBanditData, Sharer } from 'src/app/pages/petitionDetails/shared/types';
import { headlineReplacements } from 'src/app/pages/petitionDetails/shared/utilities';
import { isLoaded } from 'src/app/shared/utils/async';

import { getFacebookBanditVariantText, getSharer } from '../api';
import { CommonPetitionDetails } from '../CommonPetitionDetails';
import {
	buildMetaContext,
	petitionDetailsPageAlternateUrls,
	petitionDetailsPageCanonical,
	petitionDetailsPageMetadata,
	petitionDetailsPageSchemaOrg,
	petitionDetailsPageTitle,
} from '../shared';
import type { PetitionDetailsMetaContext } from '../shared';

type PrefetchedData = PetitionDetailsPagePrefetchedContext &
	Readonly<{
		sharer?: Sharer;
		variant?: PetitionHeadlineBanditData;
		metaContext: PetitionDetailsMetaContext;
		bestShareVisual: boolean;
	}>;

export const PetitionDetailsFacebookBandit = withPrefetchedData(
	({
		prefetchedData,
		prefetchedUserData,
	}: PrefetchedDataProps<PrefetchedData> &
		PrefetchedUserDataProps<PetitionDetailsPagePrefetchedUserContext>): JSX.Element => {
		return <CommonPetitionDetails prefetchedData={prefetchedData} prefetchedUserData={prefetchedUserData} />;
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => {
			const {
				params: { slug, experimentVariantName, sharerUserId },
			} = context;
			const [pageContext, variant, sharer] = await Promise.all([
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				prefetchPageContext(slug!, context),
				getFacebookBanditVariantText(
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					slug!,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					experimentVariantName!,
					context.utilityContext,
				).catch((): PetitionHeadlineBanditData | undefined => undefined),
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				getSharer(sharerUserId!, context.utilityContext).catch((): Sharer | undefined => undefined),
			]);

			return {
				...pageContext,
				variant,
				sharer,
				metaContext: buildMetaContext(context),
				bestShareVisual: context.path.endsWith('/bsv'),
			};
		},
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchUserData: async (context) => prefetchPageUserContext(context.params.slug!, context),
		pageTitle: (ctx) => petitionDetailsPageTitle(ctx),
		pageCanonical: (ctx) => petitionDetailsPageCanonical(ctx),
		pageMetadata: ({ prefetchedData, i18n, getStaticAssetUrl }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			return petitionDetailsPageMetadata({
				prefetchedData,
				i18n,
				getStaticAssetUrl,
				metaContext: prefetchedData.metaContext,
				bestShareVisual: prefetchedData.bestShareVisual,
				shareHeadlineOverrides: (() => {
					const replacements = headlineReplacements({
						pageData: prefetchedData.pageData.pageData,
						sharer: prefetchedData.sharer,
						i18n,
					});
					if (prefetchedData.variant) {
						return {
							title: i18n.replacePlaceholders(prefetchedData.variant.headline, replacements),
							description: i18n.replacePlaceholders(prefetchedData.variant.description, replacements),
						};
					}
					if (prefetchedData.sharer) {
						return {
							title: i18n.translate('fe.components.petition_share.share_text_fb', replacements),
							description: replacements.petition_name as string,
						};
					}
					return undefined;
				})(),
			});
		},
		pageSchemaOrg: (ctx) =>
			petitionDetailsPageSchemaOrg({
				prefetchedData: ctx.prefetchedData,
				resolveUrl: ctx.resolveUrl,
				defineArticle: ctx.defineArticle,
				defineBreadcrumb: ctx.defineBreadcrumb,
				definePerson: ctx.definePerson,
			}),
		pageAlternateUrls: (ctx) => petitionDetailsPageAlternateUrls(ctx),
	},
);
